import { useEffect, useMemo, useState } from 'react'

import { ChevronRightIcon } from '@heroicons/react/24/outline'
import classNames from 'classnames'
import { useDataProvider } from 'ra-core'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import { Tooltip } from '@mui/material'
import { useCustomChartContext } from './CustomChartState'
import { chartTypes, isMultiSeries } from './helpers'
import { ChartType, colorSchemes } from './types'

import { Card } from '../Card'
import { SectionTitle } from '../Title'
import { Button } from '../common/Button'
import { CustomChartPreview } from './CustomChartPreview'

const ColorSchemePreview = ({ colors }: { colors: string[] }) => (
  <div className="flex rounded-sm overflow-hidden flex-wrap">
    {colors.map((color) => (
      <div
        className="w-4 h-3.5"
        key={color}
        style={{ backgroundColor: color }}
      />
    ))}
  </div>
)

export const ChartTypePicker = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const dp = useDataProvider()

  const {
    state: { chartType, dataType, items, name, description, colorScheme },
    dispatch,
  } = useCustomChartContext()

  const availableChartTypes = isMultiSeries(dataType)
    ? chartTypes
    : chartTypes.filter((ct) => ct.id !== 'line')

  const createWidget = async () => {
    dp.addWidget(
      name,
      description,
      // isAvailable,
      true,
      // isCustom,
      true,
      {
        dataType,
        chartType,
        items: items?.map((i) => ({ id: i.id, name: i.name })),
        colorScheme,
      }
    ).then(() => {
      dispatch({ type: 'RESET' })
      queryClient.invalidateQueries('widgets')
    })
  }

  const canContinue = useMemo(() => {
    if (!name) return false
    if (!description) return false
    if (!dataType) return false
    if (!chartType) return false
    if (!items?.length) return false
    return !!colorScheme
  }, [chartType, colorScheme, dataType, description, items?.length, name])

  const [openTooltip, setOpenTooltip] = useState(false)

  const handleTooltipClose = () => {
    setOpenTooltip(false)
  }

  const handleTooltipOpen = () => {
    if (!canContinue) {
      setOpenTooltip(true)
    }
  }

  const selectChartType = (ct: ChartType) => {
    dispatch({ type: 'SET_CHART_TYPE', payload: { chartType: ct } })
  }

  useEffect(() => {
    if (
      availableChartTypes.length === 1 &&
      chartType !== availableChartTypes[0].id
    ) {
      dispatch({
        type: 'SET_CHART_TYPE',
        payload: { chartType: availableChartTypes[0].id },
      })
    }
  }, [availableChartTypes, chartType, dispatch])

  const previewChart = {
    chartType,
    dataType,
    items,
    colorScheme,
  }

  return (
    <div className="flex flex-col justify-between h-full">
      <div className="flex-1 flex flex-col justify-between gap-6 pb-6">
        <div className="space-y-2">
          <h3 className="text-base font-medium">
            Choose a name and description for this chart
          </h3>
          <div className="flex items-center gap-8">
            <input
              className="p-2.5 rounded-lg border border-gray-300 shadow-sm w-1/4 text-sm"
              type="text"
              placeholder="Name"
              value={name}
              onChange={(e) =>
                dispatch({
                  type: 'SET_NAME',
                  payload: { name: e.target.value },
                })
              }
            />
            <input
              className="p-2.5 rounded-lg border border-gray-300 shadow-sm w-1/3 text-sm"
              type="text"
              placeholder="Description"
              value={description}
              onChange={(e) =>
                dispatch({
                  type: 'SET_DESCRIPTION',
                  payload: { description: e.target.value },
                })
              }
            />
          </div>
        </div>
        <div className="flex gap-8">
          <div className="space-y-1">
            <h3 className="text-sm font-medium min-w-20">
              {t('custom_widgets.chart_type')}
            </h3>
            <div className="inline-flex rounded-lg border border-gray-300 shadow-sm overflow-hidden divide-x">
              {availableChartTypes.map((ct) => (
                <button
                  type="button"
                  className={classNames(
                    'py-2.5 px-3.5 active hover:bg-gray-50 flex-1',
                    {
                      'text-indigo-700 bg-indigo-50': ct.id === chartType,
                    }
                  )}
                  key={ct.id}
                  onClick={() => selectChartType(ct.id)}
                >
                  {ct.icon}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-1 col-span-2">
            <h3 className="text-sm font-medium">
              {t('custom_widgets.color_scheme')}
            </h3>
            <div className="flex rounded-lg border border-gray-300 shadow-sm overflow-hidden h-11 divide-x">
              {Object.keys(colorSchemes).map((key) => {
                const cs = colorSchemes[key]
                if (!cs) return null
                return (
                  <button
                    className={classNames(
                      'flex-1 px-3 active hover:opacity-100 hover:bg-gray-50',
                      {
                        'bg-indigo-50': key === colorScheme,
                        'opacity-60': key !== colorScheme,
                      }
                    )}
                    key={key}
                    onClick={() =>
                      dispatch({
                        type: 'SET_COLOR_SCHEME',
                        payload: { colorScheme: key },
                      })
                    }
                  >
                    <ColorSchemePreview colors={cs} />
                  </button>
                )
              })}
            </div>
          </div>
        </div>
        <div className="space-y-1 flex-1 flex flex-col justify-end">
          <h3 className="text-sm font-medium">{t('custom_widgets.preview')}</h3>
          <Card>
            <SectionTitle title={name ?? '-'} subtitle={description ?? '-'} />
            <CustomChartPreview chart={previewChart} />
          </Card>
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button
          onClick={() =>
            dispatch({ type: 'SET_CURRENT', payload: { current: 1 } })
          }
          large
          secondary
        >
          <span className="flex items-center gap-2">
            <ChevronRightIcon className="h-3 rotate-180" />
            <span>{t('custom_widgets.back')}</span>
          </span>
        </Button>
        <Tooltip
          title="You must set a title and a description"
          placement="top-end"
          open={openTooltip}
          onClose={handleTooltipClose}
          onOpen={handleTooltipOpen}
        >
          <div className="relative group">
            <Button
              disabled={!canContinue}
              onClick={() => createWidget()}
              large
            >
              <span className="flex items-center gap-2">
                <span>{t('custom_widgets.create')}</span>
                <ChevronRightIcon className="h-3" />
              </span>
            </Button>
          </div>
        </Tooltip>
      </div>
    </div>
  )
}
